'use client';

import cx from 'classix';
import { useAtomValue } from 'jotai/index';
import { useHydrateAtoms } from 'jotai/utils';

import APP_CONFIG from '@/configs/app-config';
import BlogCardListing from '@/features/blog/components/blog-card-listing/blog-card-listing';
import { isMapViewAtom } from '@/features/listing/atoms/is-map-view-atom';
import { listingsViewAtom } from '@/features/listing/atoms/listings-view-atom';
import ListingCard from '@/features/listing/components/listing-card/listing-card';
import MapViewSwitch from '@/features/listing/components/listings-filters/map-view-switch/map-view-switch';
import ListingsPagination from '@/features/listing/components/listings-section/listings-pagination/listings-pagination';
import { LISTINGS_DEFAULT_SEARCH_PARAMS } from '@/features/listing/constants/listings-filters-constants';
import { PaginationNames } from '@/features/listing/types/listing-filters-types';
import {
  type ListingCardView,
  type ListingsCollection,
  type ListingWithUser,
} from '@/features/listing/types/listing-types';

import styles from './listings-list.module.scss';

interface ListingsListProps {
  listingsCollection: ListingsCollection,
  savedListingView?: ListingCardView | null,
  blogPostPlaceOnPage?: number | null,
}

interface BlogPostTrigger {
  type: 'blogPost',
}

export default function ListingsListPagination(props: ListingsListProps) {
  const {
    blogPostPlaceOnPage,
    listingsCollection,
    savedListingView,
  } = props;

  useHydrateAtoms([[listingsViewAtom, savedListingView ?? APP_CONFIG.defaultListingCardView]]);

  const isMapView = useAtomValue(isMapViewAtom);
  const listingView = useAtomValue(listingsViewAtom);

  const listingsPerPage = Number(LISTINGS_DEFAULT_SEARCH_PARAMS[PaginationNames.PageSize]);

  // spread need to avoid add multiple blog post to listings, because of mutation object
  const cardsData: (ListingWithUser | BlogPostTrigger)[] = blogPostPlaceOnPage ? [...listingsCollection.items] : listingsCollection.items;

  if (blogPostPlaceOnPage) {
    cardsData.splice(blogPostPlaceOnPage, 0, { type: 'blogPost' });
  }

  return (
    <>
      <div>
        <div
          className={cx(
            styles.listingsList,
            styles[`listingsList__${listingView}`],
            isMapView && styles.listingsList__isMapView,
          )}
        >
          {cardsData.map((card, index) => {
            if ('type' in card) {
              return (
                <div key="blog-post" className={styles.listingsList_listingCard}>
                  <BlogCardListing />
                </div>
              );
            }

            return (
              <div key={card.id} className={styles.listingsList_listingCard}>
                <ListingCard
                  listing={card}
                  listingViewVariant={listingView}
                  messageSrc="search-grid"
                  isHighPriority={index === 0}
                />
              </div>
            );
          })}
        </div>

        <div className={styles.listingsList_mapViewSwitch}>
          <MapViewSwitch />
        </div>
      </div>

      {(listingsCollection.count > listingsPerPage) && (
        <ListingsPagination
          listingsCount={listingsCollection.count}
          listingsPerPage={listingsPerPage}
          searchGeoBbox={listingsCollection.searchGeoBbox}
        />
      )}
    </>
  );
}
